// Mobile Free Agency screen — the same swipeable ratings table as the Roster (PlayerTable), with
// a per-row "Offer" instead of "Trade". Data + actions come from the real ZenGM worker: the
// `freeAgents` view feeds props, and signing reuses ZenGM's negotiation via the offer sheet.
// Because free agency has hundreds of players, we keep a compact position + "can afford" filter
// above the table; the table's own column sort replaces the old sort pills.

import { useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { useLocal } from "../util/local.ts";
import type { View } from "../../common/types.ts";
import { toWorker } from "../util/toWorker.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { logEvent } from "../util/logEvent.ts";
import { RosterTabs } from "../components/RosterTabs.tsx";
import { PlayerTable } from "../components/PlayerTable.tsx";

const POSITIONS = ["all", "PG", "SG", "SF", "PF", "C"];

// The shared free-agent list + offer flow. `subdued` is the Roster › Free Agents tab; the default
// is the offseason Free Agency screen reached from Home during the FA phase. Both now render the
// same compact cap line + ratings table so they match the Roster.
export const FreeAgencyScreen = ({
	subdued = false,
	availableExceptions,
	capSpace,
	freeAgencySeason,
	numRosterSpots,
	payroll,
	players,
	signingExceptionAmount,
	signingExceptionLabel,
}: View<"freeAgents"> & { subdued?: boolean }) => {
	useTitleBar({ title: subdued ? "Free Agents" : "Free Agency" });

	const { minContract, maxContract, lid } = useLocal([
		"minContract",
		"maxContract",
		"lid",
	]);

	const [pos, setPos] = useState("all");
	const [affordableOnly, setAffordableOnly] = useState(false);

	// Custom offer sheet (the design's slider negotiation), wired to the real worker.
	const [offer, setOffer] = useState<{
		p: any;
		years: number;
		aav: number;
	} | null>(null);
	const [stage, setStage] = useState<"offer" | "signing" | "result">("offer");
	const [resultMsg, setResultMsg] = useState<string | null>(null);
	// Blocking notice for when a negotiation can't open — usually a free agent who refuses to sign
	// here. logEvent toasts are suppressed in this mobile UI, so without this it would be silent.
	const [notice, setNotice] = useState<string | null>(null);

	const minAav = minContract / 1000;
	const maxAav = maxContract / 1000;

	const openProfile = (pid: number) => {
		realtimeUpdate([], helpers.leagueUrl(["player", pid]));
	};

	const openOffer = async (p: any) => {
		const res = await toWorker("main", "getNegotiationProps", p.pid);
		if (typeof res === "string") {
			setNotice(res.replace(/<[^>]*>/g, "").trim());
			return;
		}
		const askYears = Math.max(
			1,
			Math.min(5, p.contract.exp - freeAgencySeason),
		);
		setResultMsg(null);
		setStage("offer");
		setOffer({
			p,
			years: askYears,
			aav: Math.max(minAav, Math.min(maxAav, p.contract.amount)),
		});
	};

	const closeOffer = async () => {
		if (offer) {
			await toWorker("main", "cancelContractNegotiation", offer.p.pid);
		}
		setOffer(null);
		setStage("offer");
		setResultMsg(null);
	};

	const submitOffer = async () => {
		if (!offer) return;
		setStage("signing");
		const exp = freeAgencySeason + offer.years;
		let err: unknown;
		try {
			err = await toWorker("main", "acceptContractNegotiation", {
				pid: offer.p.pid,
				amount: Math.round(offer.aav * 1000),
				exp,
			});
		} catch (error) {
			err =
				error instanceof Error ? error.message : "Couldn't sign this player.";
		}
		if (typeof err === "string") {
			setResultMsg(err.replace(/<[^>]*>/g, "").trim());
			setStage("result");
		} else {
			logEvent({
				type: "success",
				text: `Signed ${offer.p.firstName} ${offer.p.lastName}!`,
				saveToDb: false,
			});
			setOffer(null);
			setStage("offer");
		}
	};

	const salaryCap = capSpace + payroll;

	const available = (players as any[]).filter(
		(p) => p.freeAgentType === "available",
	);
	// "Can afford" = the asking price fits under your cap space OR under your best signing
	// exception (MLE/room/BAE) — an over-cap team can still afford MLE-priced players. Floored
	// at the minimum salary, which is always signable no matter the payroll.
	const affordLimit = Math.max(capSpace, signingExceptionAmount, minAav);
	const filtered = available.filter(
		(p) =>
			(pos === "all" || p.ratings.pos === pos) &&
			(!affordableOnly || p.contract.amount <= affordLimit),
	);

	return (
		<div className="gmfa">
			<style>{styles}</style>
			<RosterTabs active="signings" lid={lid} />

			<div className="gmfa-capline">
				<span className="gmfa-capline-cap">${capSpace.toFixed(1)}M</span>
				<span className="gmfa-capline-of">
					{" "}
					cap space of ${salaryCap.toFixed(1)}M
				</span>
				<span className="gmfa-capline-spots">
					{" "}
					· {numRosterSpots} open {numRosterSpots === 1 ? "spot" : "spots"} ·
					sign anytime
				</span>
			</div>
			{availableExceptions.length > 0 ? (
				<div className="gmfa-excline">
					Sign over the cap with:{" "}
					{availableExceptions.map((e, i) => (
						<span key={e.label} className="gmfa-capline-mle">
							{i > 0 ? " · " : ""}
							{e.label} ${e.amount.toFixed(1)}M
						</span>
					))}
				</div>
			) : null}

			<div className="gmfa-chiprow">
				{POSITIONS.map((p) => (
					<button
						key={p}
						className={`gmfa-pill ${pos === p ? "active" : ""}`}
						onClick={() => setPos(p)}
					>
						{p === "all" ? "All" : p}
					</button>
				))}
				<button
					className={`gmfa-pill gmfa-afford ${affordableOnly ? "active" : ""}`}
					onClick={() => setAffordableOnly((v) => !v)}
					title="Only show players whose asking price fits your cap space"
				>
					{affordableOnly ? "✓ " : "$ "}Can afford
				</button>
			</div>

			<div className="gmfa-intro">
				{filtered.length} {pos === "all" ? "free agents" : `${pos} free agents`}{" "}
				· tap a name for the profile
			</div>

			{filtered.length === 0 ? (
				<div className="gmfa-empty">No free agents match this filter.</div>
			) : (
				<PlayerTable
					players={filtered}
					season={freeAgencySeason}
					onName={(p) => openProfile(p.pid)}
					action={(p) => (
						<button className="pt-actbtn" onClick={() => openOffer(p)}>
							Offer
						</button>
					)}
				/>
			)}

			{offer ? (
				<OfferSheet
					offer={offer}
					stage={stage}
					resultMsg={resultMsg}
					capSpace={capSpace}
					exceptionAmount={signingExceptionAmount}
					exceptionLabel={signingExceptionLabel}
					minAav={minAav}
					maxAav={maxAav}
					onChange={setOffer}
					onSubmit={submitOffer}
					onClose={closeOffer}
				/>
			) : null}

			{notice ? (
				<div className="gmfa-sheet-overlay" onClick={() => setNotice(null)}>
					<div
						className="gmfa-sheet gmfa-notice"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="gmfa-notice-badge">✕</div>
						<div className="gmfa-notice-title">No deal</div>
						<div className="gmfa-notice-msg">{notice}</div>
						<button
							className="gmfa-sheet-submit"
							onClick={() => setNotice(null)}
						>
							Got it
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
};

const OfferSheet = ({
	offer,
	stage,
	resultMsg,
	capSpace,
	exceptionAmount,
	exceptionLabel,
	minAav,
	maxAav,
	onChange,
	onSubmit,
	onClose,
}: {
	offer: { p: any; years: number; aav: number };
	stage: "offer" | "signing" | "result";
	resultMsg: string | null;
	capSpace: number;
	exceptionAmount: number;
	exceptionLabel: string | undefined;
	minAav: number;
	maxAav: number;
	onChange: (o: { p: any; years: number; aav: number }) => void;
	onSubmit: () => void;
	onClose: () => void;
}) => {
	const { p, years, aav } = offer;
	const asking = p.contract.amount as number;
	// The player won't sign below their asking price (the worker rejects it), so
	// the salary slider is floored at the ask and the agent's reaction only ever
	// ranges from "meets the ask" to "thrilled".
	const askAav = Math.max(minAav, Math.min(maxAav, asking));
	const total = aav * years;
	const overCap = aav > capSpace;

	const reaction =
		aav >= asking * 1.1
			? { label: "They're thrilled", color: "#18E0A8", pct: 1 }
			: { label: "Meets their ask", color: "#18E0A8", pct: 0.8 };

	return (
		<div className="gmfa-sheet-overlay" onClick={onClose}>
			<div className="gmfa-sheet" onClick={(e) => e.stopPropagation()}>
				<div className="gmfa-sheet-head">
					<div>
						<div className="gmfa-sheet-name">
							{p.firstName} {p.lastName}
						</div>
						<div className="gmfa-sheet-sub">
							{p.ratings.pos} · Age {p.age} · OVR {p.ratings.ovr} · asks $
							{asking.toFixed(1)}M
						</div>
					</div>
					<button className="gmfa-sheet-x" onClick={onClose}>
						×
					</button>
				</div>

				{stage === "signing" ? (
					<div className="gmfa-sheet-status">Calling the agent…</div>
				) : stage === "result" ? (
					<>
						<div className="gmfa-sheet-status" style={{ color: "#E2504E" }}>
							{resultMsg ?? "They declined."}
						</div>
						<button className="gmfa-sheet-submit" onClick={onClose}>
							Back
						</button>
					</>
				) : (
					<>
						<div className="gmfa-slider-block">
							<div className="gmfa-slider-row">
								<span>Years</span>
								<span className="gmfa-slider-val">{years}</span>
							</div>
							<input
								type="range"
								min={1}
								max={5}
								step={1}
								value={years}
								onChange={(e) =>
									onChange({ ...offer, years: Number(e.target.value) })
								}
							/>
						</div>
						<div className="gmfa-slider-block">
							<div className="gmfa-slider-row">
								<span>Annual salary</span>
								<span className="gmfa-slider-val">${aav.toFixed(1)}M</span>
							</div>
							<input
								type="range"
								min={askAav}
								max={maxAav}
								step={0.1}
								value={aav}
								onChange={(e) =>
									onChange({ ...offer, aav: Number(e.target.value) })
								}
							/>
						</div>

						<div className="gmfa-sheet-total">
							<span>Total</span>
							<span className="gmfa-sheet-total-val">
								${total.toFixed(1)}M
								<span className="gmfa-ask-y"> / {years}y</span>
							</span>
						</div>

						<div className="gmfa-reaction">
							<span className="gmfa-fit-label">AGENT</span>
							<div className="gmfa-fit-bar">
								<div
									className="gmfa-fit-fill"
									style={{
										width: `${reaction.pct * 100}%`,
										background: reaction.color,
									}}
								/>
							</div>
							<span
								style={{ color: reaction.color, fontWeight: 700, fontSize: 12 }}
							>
								{reaction.label}
							</span>
						</div>

						{overCap ? (
							aav <= minAav + 0.05 ? (
								<div className="gmfa-cap-note">
									Minimum contract — signable even over the cap.
								</div>
							) : aav <= exceptionAmount && exceptionLabel ? (
								<div className="gmfa-cap-note">
									Over the cap — this signing uses your {exceptionLabel}.
								</div>
							) : (
								<div className="gmfa-cap-warn">
									Over your cap space
									{exceptionLabel
										? ` and your ${exceptionLabel} ($${exceptionAmount.toFixed(1)}M)`
										: ""}{" "}
									— this needs room.
								</div>
							)
						) : null}

						<button className="gmfa-sheet-submit" onClick={onSubmit}>
							Submit offer
						</button>
					</>
				)}
			</div>
		</div>
	);
};

const styles = `
.gmfa { max-width:760px; margin:0 auto; padding:16px 16px 40px; color:#fff;
  font-family:Montserrat,system-ui,sans-serif; }
.gmfa-capline { font-size:13px; font-weight:600; margin:8px 0 4px; }
.gmfa-excline { font-size:12px; font-weight:600; color:#7f8b93; margin:0 0 12px; }
.gmfa-capline-cap { color:#cfe0ec; font-weight:800; }
.gmfa-capline-of, .gmfa-capline-spots { color:#7f8b93; }
.gmfa-chiprow { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:6px; }
.gmfa-pill { border:none; border-radius:18px; padding:8px 15px; font-size:12px; font-weight:700;
  background:#181818; color:#9fb0bc; cursor:pointer; white-space:nowrap; font-family:inherit; }
.gmfa-pill.active { background:#18E0A8; color:#0a0a0a; }
/* "Can afford" is a special affordability toggle, not another position filter — set it apart:
   push it to the far right, give it an always-on green outline/tint, and glow when active. */
.gmfa-afford { margin-left:auto; border:1.5px solid #18E0A8; background:rgba(24,224,168,.10);
  color:#18E0A8; }
.gmfa-afford.active { background:#18E0A8; color:#0a0a0a; border-color:#18E0A8;
  box-shadow:0 6px 18px rgba(24,224,168,.4); }
.gmfa-intro { color:#8a8a8a; font-size:12px; margin:6px 0 2px; }
.gmfa-empty { color:#8a8a8a; text-align:center; padding:30px; }

.gmfa-sheet-overlay { position:fixed; inset:0; z-index:2000; background:rgba(0,0,0,.55);
  display:flex; align-items:flex-end; justify-content:center; }
.gmfa-sheet { width:100%; max-width:560px; background:#181818; border:1px solid #2a2a2a;
  border-radius:20px 20px 0 0; padding:20px 18px calc(20px + env(safe-area-inset-bottom));
  box-shadow:0 -20px 50px rgba(0,0,0,.5); animation:gmfaSheetUp .22s ease; font-family:Montserrat,sans-serif; }
@keyframes gmfaSheetUp { from { transform:translateY(30px); opacity:.4 } to { transform:none; opacity:1 } }
@media (min-width:640px){ .gmfa-sheet-overlay{ align-items:center } .gmfa-sheet{ border-radius:20px } }
.gmfa-sheet-head { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; }
.gmfa-sheet-name { font-weight:900; font-size:20px; }
.gmfa-sheet-sub { font-size:11px; color:#8fb3c9; margin-top:2px; }
.gmfa-sheet-x { background:none; border:none; color:#8a8a8a; font-size:26px; line-height:1; cursor:pointer; }
.gmfa-sheet-status { text-align:center; padding:26px; font-weight:700; color:#cfe0ec; }
.gmfa-slider-block { margin-bottom:16px; }
.gmfa-slider-row { display:flex; justify-content:space-between; font-size:12px; font-weight:700;
  color:#9fb0bc; margin-bottom:6px; }
.gmfa-slider-val { color:#fff; font-size:15px; }
.gmfa-sheet input[type=range] { width:100%; -webkit-appearance:none; height:5px; border-radius:4px;
  background:#232323; outline:none; }
.gmfa-sheet input[type=range]::-webkit-slider-thumb { -webkit-appearance:none; width:20px; height:20px;
  border-radius:50%; background:#18E0A8; cursor:pointer; box-shadow:0 4px 12px rgba(24,224,168,.4); }
.gmfa-sheet-total { display:flex; justify-content:space-between; align-items:baseline;
  padding:12px 0; border-top:1px solid #232323; margin-top:6px; }
.gmfa-sheet-total span:first-child { font-size:12px; font-weight:700; color:#9fb0bc; }
.gmfa-sheet-total-val { font-weight:900; font-size:22px; color:#18E0A8; }
.gmfa-ask-y { color:#7f8b93; font-weight:600; font-size:11px; }
.gmfa-reaction { display:flex; align-items:center; gap:8px; margin:8px 0 14px; }
.gmfa-fit-label { font-size:8.5px; letter-spacing:.5px; color:#7f8b93; font-weight:700; }
.gmfa-fit-bar { width:52px; height:5px; border-radius:4px; background:#232323; overflow:hidden; }
.gmfa-fit-fill { height:100%; border-radius:4px; }
.gmfa-cap-warn { font-size:12px; color:#E2504E; font-weight:700; margin-bottom:12px; }
.gmfa-cap-note { font-size:12px; color:#18E0A8; font-weight:700; margin-bottom:12px; }
.gmfa-capline-mle { color:#18E0A8; }
.gmfa-sheet-submit { width:100%; border:none; border-radius:26px; background:#18E0A8; color:#0a0a0a;
  font-weight:800; font-size:15px; padding:14px; cursor:pointer; font-family:inherit;
  box-shadow:0 10px 26px rgba(24,224,168,.3); }

.gmfa-notice { text-align:center; }
.gmfa-notice-badge { width:52px; height:52px; border-radius:50%; margin:4px auto 12px;
  display:flex; align-items:center; justify-content:center; font-size:22px; font-weight:900;
  background:rgba(226,80,78,.16); color:#E2504E; }
.gmfa-notice-title { font-size:20px; font-weight:900; letter-spacing:-.3px; margin-bottom:8px; }
.gmfa-notice-msg { font-size:14px; font-weight:600; color:#9fb0bc; line-height:1.5;
  margin:0 auto 18px; max-width:340px; }
`;

const GmFreeAgency = (props: View<"freeAgents">) => (
	<FreeAgencyScreen {...props} />
);

export default GmFreeAgency;
